"use client";

import { useState, useTransition } from "react";
import { setScoreHidden } from "../actions";
import type { ScoreRecord } from "../types";
import { BTN_GHOST, BTN_PRIMARY, INPUT, Notice, PageTitle } from "./ui";

// Launch Run's saved scores (the coming-soon game), newest first. Hiding takes a score off both leaderboards (the
// name's next best score shows instead); it stays here and can be shown again.

const FILTERS = [
  { value: "all", label: "All" },
  { value: "shown", label: "On the leaderboard" },
  { value: "hidden", label: "Hidden" },
] as const;

const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const duration = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}`;

export function ScoresAdmin({ scores: initial }: { scores: ScoreRecord[] }) {
  const [scores, setScores] = useState(initial);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("all");
  const [hiding, setHiding] = useState<{ id: string; reason: string } | null>(null);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [pending, startTransition] = useTransition();

  const change = (id: string, hidden: boolean, reason: string) =>
    startTransition(async () => {
      const result = await setScoreHidden(id, hidden, reason);
      if (result.ok) {
        setScores((list) => list.map((s) => (s.id === id ? { ...s, hidden, hiddenReason: hidden ? reason.trim() : "" } : s)));
        setHiding(null);
      }
      setNotice(result.ok ? { type: "ok", text: hidden ? "Hidden from the leaderboards." : "Back on the leaderboards." } : { type: "error", text: result.error });
    });

  const matches = (s: ScoreRecord, value: string) => value === "all" || (value === "hidden") === s.hidden;
  const shown = scores.filter((s) => matches(s, filter));
  const count = (value: string) => scores.filter((s) => matches(s, value)).length;

  return (
    <div aria-busy={pending}>
      <PageTitle title="Game scores" />
      <p className="-mt-3 mb-5 text-midnight/70">
        Scores saved from Launch Run, the game on the coming-soon page. Each name shows once on the leaderboards, with its best score. Hide a score that is
        rude or looks faked: the name&apos;s next best score takes its place.
      </p>
      <div role="tablist" aria-label="Show" className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={`${BTN_GHOST} ${filter === f.value ? "border-midnight bg-midnight text-offwhite hover:text-offwhite" : ""}`}
          >
            {f.label} ({count(f.value)})
          </button>
        ))}
      </div>
      <Notice notice={notice} />
      {shown.length ? (
        <ul className="mt-4 flex flex-col gap-3">
          {shown.map((s) => (
            <li key={s.id} className={`rounded-2xl border p-4 sm:p-5 ${s.hidden ? "border-dashed border-midnight/25 bg-offwhite" : "border-midnight/10 bg-sand"}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="text-lg font-bold">
                  <bdi>{s.nickname}</bdi>
                  {s.hidden && <span className="ms-2 rounded bg-midnight/10 px-2 py-0.5 align-middle font-mono text-xs font-normal text-midnight/80">Hidden</span>}
                </h2>
                <span className="font-mono text-2xl font-bold tabular-nums">{s.score}</span>
              </div>
              <p className="mt-1 font-mono text-xs text-midnight/70">
                {s.metres} m · {duration(s.durationMs)} from start to save · {when(s.createdAt)} (day {s.day}, Cairo) · {s.locale === "ar" ? "Arabic page" : "English page"}
              </p>
              {s.hidden && s.hiddenReason && (
                <p className="mt-2 text-sm">
                  <strong>Why hidden:</strong> <bdi>{s.hiddenReason}</bdi>
                </p>
              )}
              {hiding?.id === s.id ? (
                <form
                  className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    change(s.id, true, hiding.reason);
                  }}
                >
                  <label className="flex flex-1 flex-col gap-1 text-sm font-bold">
                    Why? (only admins see this)
                    <input value={hiding.reason} onChange={(e) => setHiding({ id: s.id, reason: e.target.value })} maxLength={200} placeholder="Rude name, faked score…" className={INPUT} />
                  </label>
                  <div className="flex gap-2">
                    <button type="submit" className={BTN_PRIMARY}>
                      Hide score
                    </button>
                    <button type="button" onClick={() => setHiding(null)} className={BTN_GHOST}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.hidden ? (
                    <button type="button" onClick={() => change(s.id, false, "")} className={BTN_GHOST}>
                      Show again
                    </button>
                  ) : (
                    <button type="button" onClick={() => setHiding({ id: s.id, reason: "" })} className={BTN_GHOST}>
                      Hide…
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-2xl border border-dashed border-midnight/20 p-8 text-center text-midnight/70">
          {scores.length ? "Nothing here." : "No scores yet. They'll appear here when players save a score at the end of a run."}
        </p>
      )}
      {scores.length >= 300 && <p className="mt-4 text-sm text-midnight/70">Showing the newest 300 scores.</p>}
    </div>
  );
}
